import {
  ACTIVITY_KIND_LABEL, DATA_STATUS_LABEL, DEFAULT_MASTER_DATA, ElectricitySourceSchema, EmissionFactorSchema, EquipmentTemplateSchema, ExampleSchema, GlossaryEntrySchema,
  MethodConfigSchema, STAGE_LABEL, TechnologySchema,
  type ElectricitySource, type EmissionFactor, type EquipmentTemplate, type Example, type GlossaryEntry, type MethodConfig, type Technology,
} from "@h2lca/core";
import type { FieldIssue } from "@/components/auth/issues";
import { fmtNum } from "@/components/admin/format";

/**
 * 마스터 데이터 종류별 표 , 서버(apps/api/src/lib/master.ts 의 KINDS)와 같은 순서,키.
 * 스키마는 @h2lca/core 의 zod 객체를 그대로 쓴다: 저장 전에 브라우저에서 한 번 거른다.
 */
export const MASTER_KINDS = ["factor", "source", "technology", "template", "method", "example", "glossary"] as const;
export type MasterKind = (typeof MASTER_KINDS)[number];
export const isMasterKind = (v: string): v is MasterKind => (MASTER_KINDS as readonly string[]).includes(v);

/** zod 스키마에서 쓰는 부분만 , web 은 zod 를 직접 의존하지 않는다 */
export interface Validator {
  safeParse(v: unknown): { success: true; data: unknown } | { success: false; error: { issues: readonly FieldIssue[] } };
}

export interface Cell {
  text: string;
  mono?: boolean;
}

export interface KindMeta {
  label: string;
  /** 키가 되는 필드 이름 , id 또는 key */
  key: string;
  schema: Validator;
  defaults: readonly Record<string, unknown>[];
  editor: "form" | "json";
  /** 새 항목의 출발점 */
  blank: () => Record<string, unknown>;
  /** 오류 경로에 붙일 한글 이름 */
  labels: Record<string, string>;
  columns: string[];
  cells: (data: unknown) => Cell[];
  hint: string;
}

const year = () => new Date().getFullYear();
const clone = <T,>(v: T): T => JSON.parse(JSON.stringify(v)) as T;

export const KIND_META: Record<MasterKind, KindMeta> = {
  factor: {
    label: "배출계수",
    key: "id",
    schema: EmissionFactorSchema,
    defaults: DEFAULT_MASTER_DATA.factors,
    editor: "form",
    blank: () => ({ id: "", name: "", kind: "electricity", value: 0, unit: "kgCO2eq/kWh", source: "", baseYear: year(), scope: "both", gwpBasis: "IPCC AR5 100년", status: "placeholder", isDefault: false, active: true }),
    labels: { id: "ID", name: "이름", kind: "활동 종류", value: "값", unit: "단위", source: "출처", sourceUrl: "출처 URL", baseYear: "기준연도", scope: "적용 범위", gwpBasis: "GWP 기준", status: "자료 상태", isDefault: "기본 계수", note: "비고", active: "사용" },
    columns: ["이름", "활동 종류", "값", "자료 상태"],
    cells: (d) => {
      const f = d as EmissionFactor;
      return [
        { text: f.name },
        { text: ACTIVITY_KIND_LABEL[f.kind] ?? String(f.kind) },
        { text: `${fmtNum(f.value)} ${f.unit}`, mono: true },
        { text: `${DATA_STATUS_LABEL[f.status] ?? String(f.status)}${f.isDefault ? ", 기본" : ""}` },
      ];
    },
    hint: "값, 단위, 출처, 기준연도가 화면과 보고서에 그대로 나갑니다. 단위는 kgCO2eq/<활동자료 단위> 꼴이어야 합니다.",
  },
  source: {
    label: "전력원",
    key: "id",
    schema: ElectricitySourceSchema,
    defaults: DEFAULT_MASTER_DATA.sources,
    editor: "form",
    blank: () => ({ id: "", label: "", efId: "", description: "", certRenewable: false, active: true }),
    labels: { id: "ID", label: "이름", efId: "배출계수", description: "설명", certRenewable: "인증제 재생에너지 인정", active: "사용" },
    columns: ["이름", "배출계수", "인증제 재생"],
    cells: (d) => {
      const s = d as ElectricitySource;
      return [{ text: s.label }, { text: s.efId, mono: true }, { text: s.certRenewable ? "인정" : "없음" }];
    },
    hint: "전력원마다 kind=electricity 인 배출계수 하나를 붙입니다. 인증제 재생에너지 인정은 교육용 단순화입니다.",
  },
  technology: {
    label: "대상기술",
    key: "id",
    schema: TechnologySchema,
    defaults: DEFAULT_MASTER_DATA.technologies,
    editor: "form",
    blank: () => ({ id: "", stage: "production", name: "", description: "", status: "ready", order: 0, active: true }),
    labels: { id: "ID", stage: "단계", name: "이름", description: "설명", status: "상태", order: "순서", active: "사용" },
    columns: ["이름", "단계", "상태", "순서"],
    cells: (d) => {
      const t = d as Technology;
      return [{ text: t.name }, { text: STAGE_LABEL[t.stage] ?? String(t.stage) }, { text: t.status === "placeholder" ? "자리만(협의 후 채움)" : "준비됨" }, { text: String(t.order), mono: true }];
    },
    hint: "팔레트에 서는 기술 목록입니다. status=placeholder 는 자리만 두고 값은 협의 후 채웁니다.",
  },
  template: {
    label: "설비 템플릿",
    key: "id",
    schema: EquipmentTemplateSchema,
    defaults: DEFAULT_MASTER_DATA.templates,
    editor: "json",
    blank: () => ({
      id: "", techId: "aec", stage: "production", name: "", description: "", icon: "box", location: "onsite", defaultInBoundary: true,
      inputs: [{ key: "electricity", label: "전력 사용량", kind: "electricity", unit: "kWh", defaultAmount: null, scaling: "perKgH2", required: true, help: "", source: "" }],
      h2: { role: "none", defaultLossRate: 0 }, help: "", order: 0, active: true,
    }),
    labels: { id: "ID", techId: "기술", stage: "단계", name: "이름", description: "설명", icon: "아이콘", location: "위치", defaultInBoundary: "기본 포함", inputs: "입력항목", h2: "수소 흐름", help: "도움말", order: "순서", active: "사용" },
    columns: ["이름", "단계", "기술", "입력항목"],
    cells: (d) => {
      const t = d as EquipmentTemplate;
      return [{ text: t.name }, { text: STAGE_LABEL[t.stage] ?? String(t.stage) }, { text: t.techId ?? "공통", mono: true }, { text: `${t.inputs?.length ?? 0}개`, mono: true }];
    },
    hint: "설비의 입력항목(kind, unit, 기본값, scaling)과 수소 흐름(role, 손실률)을 JSON으로 고칩니다. techId가 없으면 모든 기술에 보이는 공통 설비입니다.",
  },
  method: {
    label: "산정방식",
    key: "id",
    schema: MethodConfigSchema,
    defaults: DEFAULT_MASTER_DATA.methods,
    editor: "json",
    blank: () => clone(DEFAULT_MASTER_DATA.methods[0] ?? {}),
    labels: { id: "ID", label: "이름", description: "설명", standards: "적용 기준", gwp: "GWP", functionalUnit: "기능단위", boundary: "시스템 경계", denominator: "분모", grades: "등급", thresholdKgCO2eq: "기준값", notices: "안내문" },
    columns: ["이름", "분모", "적용 기준"],
    cells: (d) => {
      const m = d as MethodConfig;
      return [{ text: m.label }, { text: m.denominator === "gate" ? "생산시설 출하지점" : "최종 인도 수소량" }, { text: `${m.standards?.length ?? 0}건` }];
    },
    hint: "id는 general과 cert 둘뿐입니다. 기능단위와 경계 잠금, 등급표, 안내문을 여기서 고칩니다.",
  },
  example: {
    label: "교육용 예시",
    key: "id",
    schema: ExampleSchema,
    defaults: DEFAULT_MASTER_DATA.examples,
    editor: "json",
    blank: () => {
      const base = clone(DEFAULT_MASTER_DATA.examples[0] ?? {}) as Record<string, unknown>;
      return { ...base, id: "", name: "", description: "", tags: [], reference: null };
    },
    labels: { id: "ID", name: "이름", description: "설명", tags: "태그", scenario: "시나리오", reference: "기준결과값", order: "순서", active: "사용" },
    columns: ["이름", "태그", "설비 수"],
    cells: (d) => {
      const e = d as Example;
      return [{ text: e.name }, { text: (e.tags ?? []).join(", ") || "없음" }, { text: `${e.scenario?.equipment?.length ?? 0}개`, mono: true }];
    },
    hint: "시나리오 전체가 들어 있는 큰 JSON 입니다. 산정 화면에서 내보낸 시나리오 파일(JSON)을 scenario 에 붙여 넣는 방법이 가장 안전합니다.",
  },
  glossary: {
    label: "용어",
    key: "key",
    schema: GlossaryEntrySchema,
    defaults: DEFAULT_MASTER_DATA.glossary,
    editor: "json",
    blank: () => ({ key: "", term: "", termEn: "", definition: "", source: "", related: [] }),
    labels: { key: "키", term: "용어", termEn: "영문", definition: "정의", source: "출처", related: "관련 용어" },
    columns: ["용어", "영문", "출처"],
    cells: (d) => {
      const g = d as GlossaryEntry;
      return [{ text: g.term }, { text: g.termEn ?? "없음" }, { text: g.source }];
    },
    hint: "ⓘ 도움말에 나가는 정의입니다. 출처(표준 조항)를 꼭 적습니다.",
  },
};

export const keyOf = (kind: MasterKind, data: unknown): string => String((data as Record<string, unknown>)[KIND_META[kind].key] ?? "");
